import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LocalStorageService } from '@core/local-storage/local-storage.service';
import { environment } from '@env/environment';
import { montarFilenameDownload } from '@shared/util/util';
import { Observable, catchError, map, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  constructor(private http: HttpClient, private localStorageService: LocalStorageService) { }

  public post<T>(url: string, data: any, ignoreLoading?: boolean, headers?: HttpHeaders, params?: HttpParams) {
    params = this.setIgnoreLoading(ignoreLoading, params);
    return this.http.post<T>(this.prepareURL(url), data, { headers, params });
  }

  public postCustomApi<T>(url: string, data: any, ignoreLoading?: boolean, headers?: HttpHeaders, params?: HttpParams) {
    params = this.setIgnoreLoading(ignoreLoading, params);
    return this.http.post<T>(url, data, { headers, params })
  }

  public get<T>(url: string, ignoreLoading?: boolean, params?: HttpParams, headers?: HttpHeaders) {
    params = this.setIgnoreLoading(ignoreLoading, params);
    return this.http.get<T>(this.prepareURL(url), { headers, params });
  }

  public getCustomApi<T>(url: string, params?: HttpParams, headers?: HttpHeaders) {
    return this.http.get<T>(url, { params, headers });
  }

  public put<T>(url: string, data?: any, ignoreLoading?: boolean, params?: HttpParams, headers?: HttpHeaders) {
    params = this.setIgnoreLoading(ignoreLoading, params);
    return this.http.put<T>(this.prepareURL(url), data, { headers, params })
  }

  public putCustomApi<T>(url: string, data?: any, headers?: HttpHeaders, params?: HttpParams) {
    return this.http.put<T>(url, data, { headers, params })
  }

  public delete<T>(url: string, data?: any, ignoreLoading?: boolean, params?: HttpParams) {
    params = this.setIgnoreLoading(ignoreLoading, params);
    return this.http.request('DELETE', this.prepareURL(url), {
      params,
      body: data
    });
  }

  public patch<T>(url: string, data?: any, ignoreLoading?: boolean, params?: HttpParams, headers?: HttpHeaders) {
    params = this.setIgnoreLoading(ignoreLoading, params);
    return this.http.patch<T>(this.prepareURL(url), data, { headers, params })
  }

  public download(url: string, data?: any, ignoreLoading?: boolean, params?: HttpParams) {
    params = this.setIgnoreLoading(ignoreLoading, params);
    return this.http.post(this.prepareURL(url), data, { responseType: "blob", observe: 'response', params })
  }

  public downloadPost(url: string, data?: any, ignoreLoading?: boolean, params?: HttpParams) {
    params = this.setIgnoreLoading(ignoreLoading, params);
    return this.http.post(this.prepareURL(url), data, { responseType: "blob", observe: 'response', params })
  }

  public downloadImageAsBase64(url: string, data?: any, ignoreLoading?: boolean, params?: HttpParams) {
    params = this.setIgnoreLoading(ignoreLoading, params);
    return this.http.get(this.prepareURL(url), { responseType: 'blob', observe: 'response', params })
      .pipe(
        catchError(error => {
          console.error('Error downloading image:', error);
          return throwError(error);
        }),
        map((response: any) => {
          if (!response.ok) {
            console.error('Non-200 status code:', response.status);
            return throwError('Failed to download image');
          }

          const reader = new FileReader();
          reader.readAsDataURL(response.body);

          return new Observable(observer => {
            reader.onload = () => observer.next(reader.result as any);
            reader.onerror = error => observer.error(error);
          });
        })
      );
  }

  public downloadPostArrayBuffer(url: string, data?: any, ignoreLoading?: boolean, params?: HttpParams) {
    params = this.setIgnoreLoading(ignoreLoading, params);
    return this.http.post(this.prepareURL(url), data, { responseType: 'arraybuffer', observe: 'response', params })
      .pipe(map((response: any) => response.body));
  }

  public downloadPostBlob(url: string, data?: any, ignoreLoading?: boolean, params?: HttpParams) {
    params = this.setIgnoreLoading(ignoreLoading, params);
    return this.http.post(this.prepareURL(url), data, { responseType: 'blob', observe: 'response', params })
      .pipe(map((response: any) => response.body));
  }

  public downloadPostBlobWithFilename(url: string, data?: any, ignoreLoading?: boolean, params?: HttpParams) {
    params = this.setIgnoreLoading(ignoreLoading, params);
    return this.http.post(this.prepareURL(url), data, { 
      responseType: 'blob', 
      observe: 'response', 
      params 
    }).pipe(
      map((response: HttpResponse<Blob>) => {
        const fileName = montarFilenameDownload(response);
        return { body: response.body, fileName };
      })
    );
}

  downloadGetBlob(url: string, ignoreLoading?: boolean, params?: HttpParams): Observable<Blob> {
    params = this.setIgnoreLoading(ignoreLoading, params);
    return this.http.get(this.prepareURL(url), { responseType: 'blob', observe: 'response', params }).pipe(
      map((response: any) => response.body));
  }

  downloadGetBlobWithFilename(url: string, ignoreLoading?: boolean, params?: HttpParams): Observable<{ body: Blob | null; fileName: string }> {
    params = this.setIgnoreLoading(ignoreLoading, params);
    return this.http.get(this.prepareURL(url), { responseType: 'blob', observe: 'response', params }).pipe(
      map((response: HttpResponse<Blob>) => {
        const fileName = montarFilenameDownload(response);
        return { body: response.body, fileName };
      }),
      catchError((error) => throwError(() => new Error('Error downloading file.')))
    );
  }

  public downloadGet(url: string, headers?: HttpHeaders, ignoreLoading?: boolean, params?: HttpParams) {
    params = this.setIgnoreLoading(ignoreLoading, params);
    return this.http.get(this.prepareURL(url), { responseType: "blob", observe: 'response', headers, params })
  }

  public upload<T>(url: string, formData: FormData, ignoreLoading?: boolean, params?: HttpParams) {
    params = this.setIgnoreLoading(ignoreLoading, params);
    return this.http.post<T>(this.prepareURL(url), formData);
  }

  public uploadParams<T>(url: string, formData: FormData, ignoreLoading?: boolean, params?: HttpParams) {
    params = this.setIgnoreLoading(ignoreLoading, params);
    return this.http.post<T>(this.prepareURL(url), formData, { params });
  }

  public convertObjectToParams(obj: any): HttpParams {
    let params = new HttpParams()
    if (obj === null || obj === undefined) {
      return params;
    }

    Object.keys(obj).filter(x => obj[x] != undefined && obj[x] != null && obj[x] != 'null' && obj[x] != '').forEach((x) => {
      params = params.set(x.toString(), obj[x]);
    })

    return params;
  }

  public removeNullPropertys<T>(obj: any): T {
    Object.keys(obj).filter(x => obj[x] == undefined || obj[x] == null || obj[x] == 'null' || obj[x] == '').forEach((x) => {
      delete obj[x];
    })
    return <T>obj;
  }

  private setIgnoreLoading(ignoreLoading?: boolean, params?: HttpParams): HttpParams {
    if (ignoreLoading) {
      if (!params) {
        params = new HttpParams();
      }
      params = params.append('ignoreLoading', true);
    }

    return <HttpParams>params;
  }

  private prepareURL(url: string): string {
    if (window.location.hostname == 'localhost') {
      let urlLocal = environment.apiLocal + '/api/v1' + url;

      if (url.includes('auth')) {
        urlLocal = environment.apiLocal + '/api/' + url;
      }

      const authLocal = environment.apiLocal + url.slice(window.location.origin.length);
      return url.indexOf('//') != -1 ? authLocal : urlLocal;
    }
    else {
      // let urlAcesso = environment.api + '/api/v1' + url;

      // if(url.includes('auth')){
      //   urlAcesso = environment.api + '/api/' + url;
      // }

      let urlAcesso = environment.api + '/api/v1' + url;

      if (url.includes('auth')) {
        urlAcesso = environment.api + '/api/' + url;
      }

      return url.indexOf('//') != -1 ? url : urlAcesso;
    }
  }

}
